import { useEffect, useRef, useState } from 'react';
import { ArrowClockwise } from '@phosphor-icons/react/dist/ssr/ArrowClockwise';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import Wordmark from '../UISystem/Wordmark';
import { COLORS, FONT_FAMILY } from '../../styles/UISystem';
import { ensureSignedIn, getAuthUserId } from '../../services/supabase';
import { logEvent } from '../../services/analytics/eventLogger';
import { hideInstantSplash } from '../../utils/instantSplash';
import { isPWA } from '../../utils/installPrompt';

/**
 * "Royale can't get through" — the can't-connect screen (SYSTEM §4i, CD Drop 12
 * item 5; signup-cant-connect). Shown in place of the whole app when sign-in
 * gave up after its own retries: every read and write needs a sign-in, so
 * nothing else can load. App holders and guests see the same screen; in Safari
 * the foot sits higher, clear of the bar.
 *
 * The words never name Wi-Fi as the cause (Mark, 8 Oct): most people are on
 * mobile data, and a crowded room strains that too.
 *
 * Try again signs in again; once it works the page reloads, so the app opens
 * (or the scan carries on) with the sign-in in place.
 *
 * @param {string} cause - why sign-in gave up (getSignInFailure()), for the log
 */
const CantConnectScreen = ({ cause }) => {
  const [trying, setTrying] = useState(false);
  const taps = useRef(0);
  const shownAt = useRef(Date.now());
  const inSafari = !isPWA();

  useEffect(() => {
    // Rendered outside ScreenRouter (App.js early return) — hide the splash here
    hideInstantSplash();
  }, []);

  const tryAgain = async () => {
    taps.current += 1;
    setTrying(true);
    await ensureSignedIn();
    if (getAuthUserId()) {
      // Logged only now: the event log needs a sign-in, so a person who
      // leaves from this screen can't be recorded — recoveries are counted
      await Promise.race([
        logEvent('sign_in_recovered', {
          cause,
          taps: taps.current,
          seconds_on_screen: Math.round((Date.now() - shownAt.current) / 1000),
          in_app: !inSafari,
        }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      window.location.reload();
      return;
    }
    setTrying(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: COLORS.LIGHT_GRAY,
        fontFamily: FONT_FAMILY,
        zIndex: 1000,
      }}
    >
      <Wordmark
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top) + 7px)',
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.22em',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top) + 241px)',
          left: 28,
          right: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            textWrap: 'balance',
            color: '#000000',
          }}
        >
          Royale can’t get through
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 300,
            lineHeight: 1.45,
            textWrap: 'pretty',
            color: COLORS.DARK_GRAY,
          }}
        >
          Your connection’s busy or patchy right now. It usually clears in a moment.
        </p>
      </div>

      <div
        data-testid="cant-connect-foot"
        data-placement={inSafari ? 'safari' : 'app'}
        style={{
          position: 'absolute',
          left: 20,
          right: 20,
          // 54 above the bottom in the app, 118 in Safari (clear of its bar),
          // on a phone with a 34px home-indicator inset
          bottom: inSafari
            ? 'calc(84px + env(safe-area-inset-bottom))'
            : 'calc(20px + env(safe-area-inset-bottom))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <ButtonRounded
          text="Try again"
          icon={ArrowClockwise}
          onClick={tryAgain}
          size="full"
          variant="black"
          disabled={trying}
          style={{ height: '52px', fontSize: '17px', fontWeight: 500 }}
        />
        <div
          style={{ fontSize: 14, fontWeight: 400, color: COLORS.DARK_GRAY, textAlign: 'center' }}
        >
          Switching between Wi-Fi and mobile data can help
        </div>
      </div>
    </div>
  );
};

export default CantConnectScreen;
